class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.onKeyUp = (event) => {
      if (event.code === 'Escape' && this.classList.contains('active')) this.close();
    };
    this.onOverlayClick = () => this.close();
    this.onCartLinkClick = (event) => {
      event.preventDefault();
      this.open(this.headerCartLink);
    };
    this.onCartLinkKeydown = (event) => {
      if (event.code === 'Space' || event.code === 'Enter') {
        event.preventDefault();
        this.open(this.headerCartLink);
      }
    };

    this.addEventListener('keyup', this.onKeyUp);
    this.bindOverlay();
    this.setHeaderCartIconAccessibility();
    this.ensureViewCartLink();
  }

  disconnectedCallback() {
    this.removeEventListener('keyup', this.onKeyUp);
    if (this.overlay) this.overlay.removeEventListener('click', this.onOverlayClick);
    if (this.headerCartLink) {
      this.headerCartLink.removeEventListener('click', this.onCartLinkClick);
      this.headerCartLink.removeEventListener('keydown', this.onCartLinkKeydown);
    }
    window.clearTimeout(this.focusTrapTimer);
    document.body.classList.remove('overflow-hidden');
  }

  bindOverlay() {
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (!overlay) return;
    if (this.overlay && this.overlay !== overlay) {
      this.overlay.removeEventListener('click', this.onOverlayClick);
    }
    this.overlay = overlay;
    this.overlay.removeEventListener('click', this.onOverlayClick);
    this.overlay.addEventListener('click', this.onOverlayClick);
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    if (this.headerCartLink && this.headerCartLink !== cartLink) {
      this.headerCartLink.removeEventListener('click', this.onCartLinkClick);
      this.headerCartLink.removeEventListener('keydown', this.onCartLinkKeydown);
    }

    this.headerCartLink = cartLink;
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.setAttribute('aria-expanded', this.classList.contains('active') ? 'true' : 'false');
    cartLink.removeEventListener('click', this.onCartLinkClick);
    cartLink.removeEventListener('keydown', this.onCartLinkKeydown);
    cartLink.addEventListener('click', this.onCartLinkClick);
    cartLink.addEventListener('keydown', this.onCartLinkKeydown);
  }

  ensureViewCartLink() {
    const ctas = this.querySelector('.cart__ctas');
    if (!ctas || ctas.querySelector('[data-cart-drawer-view-cart]')) return;

    const checkout = ctas.querySelector('#CartDrawer-Checkout, [name="checkout"]');
    if (!checkout) return;

    const viewCart = document.createElement('a');
    viewCart.href = window.routes?.cart_url || '/cart';
    viewCart.className = 'btn btn--secondary cart-drawer__view-cart';
    viewCart.dataset.cartDrawerViewCart = '';
    viewCart.textContent = document.documentElement.lang?.toLowerCase().startsWith('ar') ? 'عرض السلة' : 'View cart';
    viewCart.setAttribute('aria-label', viewCart.textContent);
    ctas.insertBefore(viewCart, checkout);
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    this.ensureViewCartLink();
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    this.classList.add('animate');
    requestAnimationFrame(() => this.classList.add('active'));
    if (this.headerCartLink) this.headerCartLink.setAttribute('aria-expanded', 'true');
    document.body.classList.add('overflow-hidden');

    const activateFocusTrap = () => {
      if (!this.classList.contains('active')) return;
      const containerToTrapFocusOn = this.classList.contains('is-empty')
        ? this.querySelector('.drawer__inner-empty')
        : this.querySelector('#CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      if (containerToTrapFocusOn && focusElement) trapFocus(containerToTrapFocusOn, focusElement);
    };

    this.addEventListener('transitionend', activateFocusTrap, { once: true });
    window.clearTimeout(this.focusTrapTimer);
    this.focusTrapTimer = window.setTimeout(activateFocusTrap, 380);
  }

  close() {
    if (!this.classList.contains('active')) return;
    this.classList.remove('active');
    if (this.headerCartLink) this.headerCartLink.setAttribute('aria-expanded', 'false');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    window.clearTimeout(this.focusTrapTimer);
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling?.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement?.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    if (!parsedState?.sections) return;

    this.classList.remove('is-empty');
    const drawerInner = this.querySelector('.drawer__inner');
    if (drawerInner?.classList.contains('is-empty')) drawerInner.classList.remove('is-empty');
    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      const sectionHTML = parsedState.sections[section.id];
      if (!sectionElement || !sectionHTML) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(sectionHTML, section.selector);
    });

    this.bindOverlay();
    this.setHeaderCartIconAccessibility();
    this.ensureViewCartLink();
    this.open();
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const documentFragment = new DOMParser().parseFromString(html, 'text/html');
    const target = documentFragment.querySelector(selector);
    return target ? target.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
        childSectionSelectors: [
          '.drawer__heading',
          '.cart__free-shipping',
          '.drawer__contents',
          '.cart-drawer__footer-info'
        ]
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  constructor() {
    super();
    this.cartDrawerUpsells = this.querySelector('.cart-drawer-recommendations');
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.js-contents',
        childSectionSelectors: [
          '.drawer__heading',
          '.cart__free-shipping',
          '.drawer__contents',
          '.cart-drawer__footer-info'
        ]
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }

  closeUpsells(cartDrawerInner) {
    if (!this.cartDrawerUpsells) return;
    this.cartDrawerUpsells.style.transform = 'translate(100%)';
    this.cartDrawerUpsells.addEventListener('transitionend', () => {
      if (cartDrawerInner) cartDrawerInner.classList.remove('drawer__inner--no-border');
      this.cartDrawerUpsells.classList.add('close');
    }, { once: true });
  }

  updateUpsells(cartRecommendationsHtml, cartDrawerInner) {
    if (!this.cartDrawerUpsells) return;
    if (cartRecommendationsHtml === '') {
      this.cartDrawerUpsells.classList.remove('cart-drawer-recommendations--loading');
      this.closeUpsells(cartDrawerInner);
    } else {
      const cartDrawerRecommendationsList = this.cartDrawerUpsells.querySelector('.cart-drawer-recommendations__list');
      if (cartDrawerRecommendationsList) {
        cartDrawerRecommendationsList.classList.add('disable-list');
        if (cartDrawerInner) cartDrawerInner.classList.add('drawer__inner--no-border');
        this.cartDrawerUpsells.classList.remove('close');
      }

      setTimeout(() => {
        this.cartDrawerUpsells.innerHTML = cartRecommendationsHtml;
      }, 200);
    }
  }
}

if (!customElements.get('cart-drawer-items')) customElements.define('cart-drawer-items', CartDrawerItems);

class CartRecommendations extends HTMLElement {
  connectedCallback() {
    if (!this.dataset.url || this.dataset.loading === 'true') return;
    this.dataset.loading = 'true';

    fetch(this.dataset.url)
      .then((response) => {
        if (!response.ok) throw new Error(`Cart recommendations request failed: ${response.status}`);
        return response.text();
      })
      .then((text) => {
        if (this.classList.contains('product-recommendations--loaded')) return;

        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('cart-recommendations');
        const cartDrawerUpsells = this.closest('.cart-drawer-recommendations');
        if (!cartDrawerUpsells) return;

        if (recommendations && recommendations.innerHTML.trim().length) {
          cartDrawerUpsells.classList.remove('cart-drawer-recommendations--loading');
          this.innerHTML = recommendations.innerHTML;
          const cartDrawerRecommendationsList = this.querySelector('.cart-drawer-recommendations__list');

          if (cartDrawerRecommendationsList) {
            setTimeout(() => {
              this.querySelector('.cart-drawer-recommendations__list')?.classList.remove('disable-list');
            });
          }
        }

        const cartDrawerInner = cartDrawerUpsells.closest('.drawer__inner');
        if (!this.querySelector('.cart-drawer-recommendations__list')) {
          cartDrawerUpsells.classList.add('close');
          setTimeout(() => {
            if (cartDrawerInner) cartDrawerInner.classList.remove('drawer__inner--no-border');
          }, 200);
        } else {
          if (cartDrawerInner) cartDrawerInner.classList.add('drawer__inner--no-border');
          cartDrawerUpsells.classList.remove('close');
        }

        this.classList.add('product-recommendations--loaded');

        if (this.classList.contains('complementary-products')) {
          this.querySelectorAll('script').forEach((oldScriptTag) => {
            const newScriptTag = document.createElement('script');
            Array.from(oldScriptTag.attributes).forEach((attribute) => {
              newScriptTag.setAttribute(attribute.name, attribute.value);
            });
            newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
            oldScriptTag.parentNode?.replaceChild(newScriptTag, oldScriptTag);
          });
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        delete this.dataset.loading;
      });
  }
}

if (!customElements.get('cart-recommendations')) customElements.define('cart-recommendations', CartRecommendations);
